import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const DatabaseQueryConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['connection', 'query']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const dbTypes = [
    { value: 'mysql', label: 'MySQL', icon: '🐬' },
    { value: 'postgresql', label: 'PostgreSQL', icon: '🐘' },
    { value: 'mongodb', label: 'MongoDB', icon: '🍃' },
    { value: 'mssql', label: 'MS SQL Server', icon: '🗄️' },
    { value: 'sqlite', label: 'SQLite', icon: '📦' },
  ];

  return (
    <div className="space-y-3">
      {/* Kapcsolat */}
      <CollapsibleSection
        id="connection"
        title="Adatbázis Kapcsolat"
        icon="🔌"
        color="indigo"
        isExpanded={expandedSections.has('connection')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Adatbázis típus
            </label>
            <div className="grid grid-cols-2 gap-2">
              {dbTypes.map((db) => (
                <button
                  key={db.value}
                  onClick={() => updateConfig('dbType', db.value)}
                  className={`flex items-center p-2 rounded-md border text-sm transition-all ${
                    (node.data.config.dbType || 'mysql') === db.value
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <span className="text-lg mr-2">{db.icon}</span>
                  <span className="text-xs">{db.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={commonStyles.label}>
              Connection String
            </label>
            <input
              type="text"
              value={node.data.config.connectionString || ''}
              onChange={(e) => updateConfig('connectionString', e.target.value)}
              className={commonStyles.input}
              placeholder="mysql://user:pass@localhost:3306/dbname vagy {{connection}}"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Kapcsolati string vagy változó
            </p>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
            <div className="text-xs text-yellow-700 dark:text-yellow-300">
              <strong>⚠️ Biztonság:</strong> Használj környezeti változókat az érzékeny adatokhoz!
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Query */}
      <CollapsibleSection
        id="query"
        title="SQL Lekérdezés"
        icon="📝"
        color="blue"
        isExpanded={expandedSections.has('query')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              SQL Query
            </label>
            <textarea
              value={node.data.config.query || ''}
              onChange={(e) => updateConfig('query', e.target.value)}
              className={`${commonStyles.textarea} font-mono text-xs`}
              rows={6}
              placeholder={`SELECT * FROM users WHERE status = 'active'

vagy használj paramétereket:
SELECT * FROM users WHERE id = {{user_id}}`}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              SQL lekérdezés változókkal
            </p>
          </div>

          <div>
            <label className={commonStyles.label}>
              Paraméterek (JSON)
            </label>
            <textarea
              value={node.data.config.parameters || ''}
              onChange={(e) => updateConfig('parameters', e.target.value)}
              className={`${commonStyles.textarea} font-mono text-xs`}
              rows={3}
              placeholder='{"user_id": 123, "status": "active"}'
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Opcionális query paraméterek
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="returnFirst"
              checked={node.data.config.returnFirst || false}
              onChange={(e) => updateConfig('returnFirst', e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="returnFirst" className="text-sm text-gray-700 dark:text-gray-300">
              Csak első eredmény visszaadása
            </label>
          </div>
        </div>
      </CollapsibleSection>

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A lekérdezés után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{rows}}', desc: 'Eredmény sorok', example: '[{...}, {...}]' },
              { var: '{{count}}', desc: 'Sorok száma', example: '42' },
              { var: '{{first}}', desc: 'Első sor', example: '{id: 1, ...}' },
              { var: '{{success}}', desc: 'Sikeres lekérdezés', example: 'true' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="green"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📊 Adatok lekérdezése:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Database Query → Data Transform → Email
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🔍 Felhasználó keresés:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Webhook → Database Query → Condition → Response
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📈 Report generálás:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Schedule → Database Query → CSV Parser → Email
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default DatabaseQueryConfig;
